import {
  Badge,
  Box,
  Button,
  HStack,
  IconButton,
  Spinner,
  Stack,
  Stat,
  StatGroup,
  StatHelpText,
  StatLabel,
  StatNumber,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
  Wrap,
  WrapItem,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FaPlus, FaEdit, FaFileImport, FaFile, FaTags } from "react-icons/fa";
import { FiEdit2, FiRefreshCw, FiTrash2 } from "react-icons/fi";
import actions, { KYDUserWithGuestlist } from "../../../../actions";

import { useLoading } from "../../../hooks/form";
import { useDialogWithOptions } from "../../../../Common/Dialog";
import GuestlistDialog, {
  GuestlistDialogContext,
  withGuestlistDialog,
} from "./GuestlistDialog";
import GuestlistAddEditUserDialogMobile, {
  GuestlistAddUserDialogContext,
  withAddUserDialog,
} from "./GuestlistUserAddDialog";
import ConfirmDialog, {
  ConfirmDialogContext,
  withConfirmDialog,
} from "../../ConfirmDialog";
import { matchPath, useLocation, useParams } from "react-router-dom";
import { useKydEvent } from "../../hooks/useKydEvent";
import { useGuestlist } from "../../hooks/useGuestlist";
import {
  createOnUpdateEvent,
  EditModalType,
  isTicketTypeSystem,
} from "../../../../utils";
import ReactS3Uploader from "react-s3-uploader";
import EventSummarySection from "../../SummarySection/EventSummarySection";
import { EventDetailSummarySection } from "@common/interfaces/KYDEvent";
import { ActionType, useDialog } from "../../../../Common/Dialog";
import ColumnMatchingModal, {
  ColumnMatchingModalContext,
  withColumnMatchingModal,
} from "./ColumnMatchingDialog";
import { GuestlistTagCountsModal } from "./GuestlistTagCountsModal";

type GuestlistCreateUser = {
  id?: string;
  phone_number: string;
  quantity: number;
  ticket_type_id: string;
  guestlist_first_name: string;
  guestlist_last_name: string;
  notes: string;
  tags: string[];
};

const GuestlistNavSection = () => {
  const { event_id } = useParams();
  const { kydEvent, refetch } = useKydEvent(event_id, {
    sectionTypes: ["summary", "guestlist"],
  });
  const location = useLocation();
  const guestlistSectionData = useMemo(() => {
    const guestlistSection = kydEvent?.nav
      ?.find((nav) => matchPath(nav.url, location.pathname))
      ?.sections.find((sec) => sec.type === "guestlist");
    return guestlistSection?.data as any;
  }, [kydEvent, location.pathname]);

  const hasInitialGuestlistData = Boolean(
    guestlistSectionData?.guestlist_allocation &&
      guestlistSectionData?.guestlist_users
  );
  const [modalType, setModalType] = useState(EditModalType.INACTIVE);
  const downloadTemplateRef = useRef<HTMLAnchorElement | null>(null);
  const { open: openColumnMatchingDialog, close: closeColumnMatchingDialog } =
    useDialogWithOptions(ColumnMatchingModalContext);

  const [guestlistColumns, setGuestlistColumns] = useState<{
    sample: any[];
    schema_fields: Array<{ is_match: boolean; column: string } | null>;
    file_key: string;
    saved_schema_fields: string[];
  }>({
    sample: [],
    schema_fields: [],
    file_key: "",
    saved_schema_fields: [],
  });

  const [loading, setLoading] = useLoading({
    init: false,
    csv: false,
    event: false,
    guestlist: false,
  });
  const {
    users: guestlistUsers,
    guestlist: allocation,
    tagCounts,
    updateGuestlistAllocation,
    refetch: refetchGuestlist,
    cache: { setUsers: setGuestlistUsers, setGuestlist },
    loading: isGuestlistLoading,
  } = useGuestlist(event_id, {
    autoFetch: !!kydEvent && !hasInitialGuestlistData,
    initialData: {
      allocation: guestlistSectionData?.guestlist_allocation,
      users: guestlistSectionData?.guestlist_users,
    },
  });

  const [allocationErrors, setAllocationErrors] = useState({ quantity: null });
  const [creatingLoading, setCreatingLoading] = useLoading({
    creating: false,
  });
  const dialog = useDialog();
  const {
    open: openGuestlistAllocationDialog,
    close: closeGuestlistAllocationDialog,
  } = useDialogWithOptions(GuestlistDialogContext);
  const {
    open: openGuestlistAddEditUserDialog,
    close: closeGuestlistAddEditUserDialog,
  } = useDialogWithOptions(GuestlistAddUserDialogContext);
  const confirmDialog = useDialogWithOptions(ConfirmDialogContext);

  const openMessageDialog = useDialog();
  const [toDelete, setToDelete] = useState<{
    index: number;
    event_id: string;
    ticket_type_id: string;
    phone_number: string;
    user_id: string;
  }>();
  const [toEdit, setToEdit] = useState<KYDUserWithGuestlist>();
  const toast = useToast();
  const {
    isOpen: isTagCountsOpen,
    onOpen: openTagCounts,
    onClose: closeTagCounts,
  } = useDisclosure();

  const onGuestlistUserUpdate = async (
    values: GuestlistCreateUser,
    mode: "create" | "edit"
  ) => {
    const toSave = values;

    console.log({ toSave });
    setCreatingLoading("creating", true);
    try {
      const phone_number = toSave.phone_number;
      const response = await actions.ADMIN.createGuestlistUser({
        event_id: kydEvent?.id,
        id: values.id,
        ticket_type_id:
          values.ticket_type_id === "SS" ? undefined : values.ticket_type_id,
        quantity: 1,
        ...toSave,
        phone_number,
        mode,
      });

      const hasError =
        response.did_reserve !== true ||
        response.validation_result.is_valid !== true;

      if (hasError) {
        openMessageDialog({
          title: "Error",
          text: response.message || response.validation_result?.message,
        });
        return;
      }

      setCreatingLoading("creating", false);

      if (response.did_reserve) {
        // @ts-ignore
        setGuestlistUsers((users) => {
          const exists = users.find((u) => u.id === response.user_id);

          if (exists) {
            return users.map((u) => {
              if (u.id === response.user_id) {
                return {
                  ...u,
                  first_name: toSave.guestlist_first_name,
                  last_name: toSave.guestlist_last_name,
                  guestlist: {
                    ticket_type_id: toSave.ticket_type_id,
                    assigned: toSave.quantity,
                    status: "PROCESSING",
                  },
                  notes: toSave.notes,
                  kyd_tags: toSave.tags,
                  tags: toSave.tags,
                };
              }

              return u;
            });
          }

          const phone_number = toSave.phone_number;

          return [
            {
              id: response.user_id,
              phone_number: `${phone_number}`,
              guestlist: {
                ticket_type_id: toSave.ticket_type_id,
                assigned: toSave.quantity,
                status: "PROCESSING",
                created_at: new Date().toISOString(),
              },
              first_name: toSave.guestlist_first_name,
              last_name: toSave.guestlist_last_name,
              notes: toSave.notes,
              tags: toSave.tags,
            } as KYDUserWithGuestlist,
            ...users,
          ];
        });
      }
    } catch (e) {
      openMessageDialog({
        title: "Error",
        text: e.message,
      });
    } finally {
      setToEdit(null);
      closeGuestlistAddEditUserDialog();
      setCreatingLoading("creating", false);
    }

    return;
  };

  const isCsvImportInProgress = useMemo(() => {
    if (!allocation) return false;

    return allocation.allocations.some(
      (entry) =>
        isTicketTypeSystem(entry.ticket_type_id) && entry.is_processing
    );
  }, [allocation]);

  const allTicketTypes = useMemo(() => {
    if (!kydEvent) return [];

    return (kydEvent.ticket_types || []).concat(
      kydEvent.tiered_ticket_types || []
    );
  }, [kydEvent]);

  const getTicketTypeName = useCallback(
    (ticket_type_id: string) => {
      if (!kydEvent) return "";

      if (isTicketTypeSystem(ticket_type_id) || !ticket_type_id)
        return "Unassigned";

      return allTicketTypes.find((t) => t.id === ticket_type_id)?.name;
    },
    [allTicketTypes]
  );

  const onDeleteGuestlistUser = useCallback(
    async (
      index: number,
      event_id: string,
      ticket_type_id: string,
      phone_number: string,
      user_id: string,
      name: string
    ) => {
      setToDelete({
        index,
        event_id: event_id,
        ticket_type_id,
        phone_number,
        user_id,
      });
      confirmDialog.open({
        title: `Delete ${name}?`,
        text: `Are you sure you want to remove ${name} from guestlist? You can add them back later`,
      });
    },
    [setToDelete]
  );

  const confirmDeleteGuestlistUser = useCallback(async () => {
    if (!toDelete) return;

    const { event_id, ticket_type_id, phone_number, user_id, index } = toDelete;
    setCreatingLoading("creating", true);

    try {
      await actions.ADMIN.deleteGuestlistUser({
        user_id,
        event_id,
        ticket_type_id,
        phone_number,
      });

      //@ts-ignore
      setGuestlistUsers((users) => {
        return users.map((user) => {
          if (user.id === user_id) {
            return {
              ...user,
              guestlist: {
                ...user.guestlist,
                status: "REMOVING",
              },
            };
          }

          return user;
        });
      });
    } catch (err) {
      openMessageDialog({
        title: "Error",
        text: err.message,
      });
    }
    setToDelete(null);
    setCreatingLoading("creating", false);
  }, [toDelete, setGuestlistUsers]);

  const onGuestlistColumnsMatched = useCallback(
    async ({ schema_fields, file_key }) => {
      closeColumnMatchingDialog();
      const response = await actions.ADMIN.guestlistImportCsv(
        kydEvent?.id,
        file_key,
        schema_fields
      );

      setLoading("guestlist", true);
      if (response.is_valid) {
        setGuestlistColumns({
          file_key: null,
          schema_fields: [],
          sample: [],
          saved_schema_fields: [],
        });
        setGuestlist((guestlist) => {
          return {
            ...guestlist,
            allocations: guestlist.allocations.map((allocation) => {
              if (isTicketTypeSystem(allocation.ticket_type_id)) {
                return {
                  ...allocation,
                  is_processing: true,
                };
              }

              return allocation;
            }),
          };
        });

        toast({
          title: "CSV Import started",
          description: `Your import is now processing. Check in a few minutes to see the status.`,
          status: "success",
          duration: 5000,
          isClosable: true,
        });
      } else if (!response.is_valid) {
        dialog({
          title: "Invalid file",
          text: response.message,
        });
      }
    },
    [kydEvent]
  );

  const onGuestlistFileChange = useCallback(
    async (fileKey: string) => {
      if (!fileKey) return;

      setLoading("csv", true);

      try {
        const response = await actions.ADMIN.fetchFileColumns(
          kydEvent?.id,
          fileKey
        );

        console.log({ response });

        setGuestlistColumns({
          file_key: fileKey,
          schema_fields: response.schema_fields,
          sample: response.sample,
          saved_schema_fields: [],
        });
        openColumnMatchingDialog();
      } catch (err) {
        console.error(err);
        toast({
          title: "Error",
          description: err.message,
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setLoading("csv", false);
      }
    },
    [actions, kydEvent, setLoading]
  );

  useEffect(() => {
    const try_for_times = 5;
    let tried = 0;
    let delay = 2000;
    let id: NodeJS.Timeout;
    function tick() {
      const ret = refetchGuestlist({ silent: true });
      tried++;

      if (ret instanceof Promise) {
        ret.then(() => {
          if (delay !== null) {
            id = setTimeout(tick, delay);
          }
        });
      } else {
        if (delay !== null) {
          id = setTimeout(tick, delay);
        }
      }
    }

    if (
      (allocation &&
        allocation.allocations &&
        allocation.allocations.filter((a) => a.is_processing).length > 0) ||
      (guestlistUsers &&
        guestlistUsers.filter(
          (gu) =>
            gu.guestlist.status === "REMOVING" ||
            gu.guestlist.status === "PROCESSING"
        ).length > 0) ||
      isCsvImportInProgress
    ) {
      setLoading("init", true);
      setLoading("guestlist", false);
      if (delay !== null) {
        id = setTimeout(tick, delay);
      }
    } else {
      console.log({ tried, try_for_times });
      if (loading.guestlist && tried < try_for_times) {
        // Try again
        if (delay !== null) {
          id = setTimeout(tick, delay);
        }
      } else {
        console.log("No processsing");
        setLoading("guestlist", false);
      }
    }

    return () => id && clearTimeout(id);
  }, [allocation, guestlistUsers, setLoading]);

  const allTickets = allocation?.allocations.reduce(
    (allTickets, allocation) => {
      return {
        assigned: allTickets.assigned + allocation.assigned,
      };
    },
    {
      assigned: 0,
    }
  );

  // Hack to have permissions in guestlist without full refactor
  const hasWritePermissions = kydEvent?.nav
    .find((nav) => matchPath(nav.url, location.pathname))
    ?.sections.find(
      (sec) => sec.type === "guestlist" && sec.permissions.includes("write")
    );

  const eventSummarySection = kydEvent?.nav
    .find((nav) => matchPath(nav.url, location.pathname))
    ?.sections.find(
      (sec) => sec.type === "summary"
    ) as EventDetailSummarySection;

  const onCloseColumnMatchingDialog = useCallback(() => {
    closeColumnMatchingDialog();
    setGuestlistColumns({
      file_key: "",
      schema_fields: [],
      sample: [],
      saved_schema_fields: [],
    });
  }, [closeColumnMatchingDialog]);

  const onUpdateEvent = useCallback(
    createOnUpdateEvent({
      dialog,
      event_id,
      refetch,
      setLoading: (isLoading) => setLoading("event", isLoading),
      setModalType,
      toast,
    }),
    [event_id, refetch, setLoading, dialog, toast, setModalType]
  );

  return (
    <Stack maxW="5xl" w="full">
      <ColumnMatchingModal
        title="Import Guestlist"
        guestlistColumns={guestlistColumns}
        onClose={onCloseColumnMatchingDialog}
        onSave={onGuestlistColumnsMatched}
        onGuestlistFileChange={onGuestlistFileChange}
      />
      {eventSummarySection ? (
        <EventSummarySection
          isLoading={isGuestlistLoading || loading.event}
          data={eventSummarySection?.data as EventDetailSummarySection["data"]}
          permissions={(eventSummarySection?.permissions || []).filter(
            (permission) => permission !== "write"
          )}
          onUpdateEvent={onUpdateEvent}
          modalType={modalType}
          setModalType={setModalType}
          refetchEvent={refetch}
        />
      ) : null}
      {!kydEvent?.nav ? (
        <VStack w="100%">
          <Spinner />
        </VStack>
      ) : (
        <>
          {kydEvent?.status === "DRAFT" ? (
            <VStack
              rounded={"lg"}
              borderWidth={"1px"}
              borderColor={"blue.300"}
              bg="blue.100"
              w="100%"
              maxW={""}
              p={4}
            >
              <Text color="blue.700">
                Guestlist will be available after you launch this event
              </Text>
            </VStack>
          ) : null}
          <GuestlistDialog
            allocationErrors={allocationErrors}
            isLoading={isGuestlistLoading}
            ticket_types={allTicketTypes.filter((tt) => tt.status === "OPEN")}
            onSave={(data) => {
              setLoading("allocation", true);

              updateGuestlistAllocation(data)
                .then(() => {
                  closeGuestlistAllocationDialog();
                })
                .catch((e) => {
                  setAllocationErrors({ quantity: e });
                })
                .finally(() => {
                  setLoading("allocation", false);
                });
            }}
            allocation={allocation}
          />
          <GuestlistAddEditUserDialogMobile
            isLoading={creatingLoading.creating}
            allocationErrors={{}}
            allocation={allocation}
            creatingLoading={creatingLoading.creating}
            onSave={onGuestlistUserUpdate}
            getTicketTypeName={getTicketTypeName}
            isEdit={!!toEdit}
            guestlistUser={toEdit}
            onClose={() => {
              setToEdit(null);
              closeGuestlistAddEditUserDialog();
            }}
          />
          <ConfirmDialog
            onConfirm={confirmDeleteGuestlistUser}
            onCancel={() => setToDelete(undefined)}
          />
          <GuestlistTagCountsModal
            isOpen={isTagCountsOpen}
            onClose={closeTagCounts}
            tagCounts={tagCounts}
          />
          <VStack mt={8}>
            <VStack
              w="full"
              alignItems={"flex-start"}
              justifyContent={"space-between"}
            >
              <Stack
                direction={["column", "row"]}
                alignItems={"flex-start"}
                justifyContent={"space-between"}
                w="full"
              >
                <Box justifyContent={"space-between"}>
                  <Text fontWeight={"bold"}>People</Text>
                </Box>

                <Stack
                  direction={["column-reverse", "row"]}
                  w="full"
                  justifyContent={"flex-end"}
                >
                  <Stack direction={["column", "row"]}>
                    <Button
                      onClick={openTagCounts}
                      variant="outline"
                      leftIcon={<FaTags />}
                    >
                      Tags
                    </Button>
                    {kydEvent?.display_options?.guestlist_template_url ? (
                      <>
                        <a
                          ref={downloadTemplateRef}
                          hidden
                          href={
                            kydEvent?.display_options.guestlist_template_url
                          }
                          download="guestlist-import-template.csv"
                        />
                        {hasWritePermissions ? (
                          <Button
                            onClick={() => {
                              if (downloadTemplateRef.current) {
                                downloadTemplateRef.current.click();
                              }
                            }}
                            aria-label="Download template"
                            variant={"outline"}
                            leftIcon={<FaFile />}
                          >
                            Download Template
                          </Button>
                        ) : null}
                      </>
                    ) : null}

                    {hasWritePermissions ? (
                      <>
                        <Button
                          isDisabled={loading.csv || isCsvImportInProgress}
                          isLoading={loading.csv}
                          onClick={() => {
                            openColumnMatchingDialog();
                          }}
                          // onClick={() => {
                          //   if (uploadRef.current) {
                          //     uploadRef.current.click();
                          //   }
                          // }}
                          aria-label="Download event report"
                          variant={"outline"}
                          leftIcon={<FaFileImport />}
                        >
                          {isCsvImportInProgress
                            ? "Import in progress"
                            : "Import CSV"}
                        </Button>
                      </>
                    ) : null}
                  </Stack>
                  {hasWritePermissions ? (
                    <IconButton
                      variant={"outline"}
                      aria-label="Add guestlist user"
                      icon={<FaPlus />}
                      onClick={() => {
                        openGuestlistAddEditUserDialog();
                      }}
                    />
                  ) : null}
                </Stack>
              </Stack>
            </VStack>
            <StatGroup borderColor="gray.100" rounded={"lg"} w="100%">
              <Stat>
                <StatLabel>Total People</StatLabel>
                <StatNumber>
                  {isGuestlistLoading ? "-" : allocation?.unique_people}
                </StatNumber>
                <StatHelpText maxW={"80%"} fontSize={"xs"}>
                  Number of people you've added to the guestlist
                </StatHelpText>
              </Stat>
              <Stat>
                <StatLabel>Assigned Tickets</StatLabel>
                <StatNumber>
                  {isGuestlistLoading ? "-" : allTickets.assigned}
                </StatNumber>
                <StatHelpText maxW={"80%"} fontSize={"xs"}>
                  Number of tickets you've assigned to people
                </StatHelpText>
              </Stat>
            </StatGroup>
            <Box w="full" overflowX="scroll">
              <Table width={"full"}>
                <Thead w="full">
                  <Tr>
                    <Th>Name</Th>
                    <Th>Tags</Th>
                    <Th>Tickets</Th>
                    <Th>Status</Th>
                    <Th></Th>
                  </Tr>
                </Thead>
                <Tbody w="full">
                  {isCsvImportInProgress ? (
                    <Tr>
                      <Td colSpan={5}>
                        <HStack
                          gap={4}
                          w="full"
                          alignItems={"center"}
                          justifyContent={"center"}
                        >
                          <Spinner />
                          <Text>Guestlist is updating</Text>
                        </HStack>
                      </Td>
                    </Tr>
                  ) : null}
                  {guestlistUsers.map((guestlistUser, index) => (
                    <Tr key={guestlistUser.id}>
                      <Td>
                        {guestlistUser.first_name} {guestlistUser.last_name}
                      </Td>
                      <Td maxW="xs">
                        <Wrap spacing={1}>
                          {(guestlistUser.tags || []).map((tag, tagIndex) => (
                            <WrapItem key={`${guestlistUser.id}-${tagIndex}`}>
                              <Badge>{tag}</Badge>
                            </WrapItem>
                          ))}
                        </Wrap>
                      </Td>
                      <Td>{guestlistUser.guestlist?.assigned}</Td>
                      <Td>
                        <Badge
                          colorScheme={
                            guestlistUser.guestlist?.status === "ASSIGNED"
                              ? "green"
                              : "gray"
                          }
                        >
                          {guestlistUser.guestlist?.status}
                        </Badge>
                      </Td>
                      <Td>
                        <HStack spacing="1">
                          {guestlistUser.guestlist?.status !== "ASSIGNED" ? (
                            hasWritePermissions ? (
                              <IconButton
                                onClick={() => refetchGuestlist()}
                                icon={<FiRefreshCw fontSize="1.25rem" />}
                                variant="ghost"
                                aria-label="Refresh status"
                              />
                            ) : null
                          ) : null}
                          {hasWritePermissions ? (
                            <>
                              <IconButton
                                isDisabled={
                                  !allocation ||
                                  guestlistUser?.guestlist?.status ===
                                    "PROCESSING" ||
                                  (toDelete && toDelete.index === index) ||
                                  (isTicketTypeSystem(
                                    guestlistUser?.guestlist?.ticket_type_id
                                  ) &&
                                    isCsvImportInProgress)
                                }
                                variant={"ghost"}
                                aria-label="Edit guestlist user"
                                icon={<FaEdit />}
                                onClick={() => {
                                  setToEdit(guestlistUser);
                                  openGuestlistAddEditUserDialog();
                                }}
                              />
                              <IconButton
                                onClick={() =>
                                  onDeleteGuestlistUser(
                                    index,
                                    kydEvent?.id,
                                    guestlistUser.guestlist.ticket_type_id,
                                    guestlistUser.phone_number,
                                    guestlistUser.id,
                                    guestlistUser.first_name
                                  )
                                }
                                isDisabled={
                                  guestlistUser?.guestlist?.status ===
                                    "PROCESSING" ||
                                  (toDelete && toDelete.index === index) ||
                                  (isTicketTypeSystem(
                                    guestlistUser?.guestlist?.ticket_type_id
                                  ) &&
                                    isCsvImportInProgress)
                                }
                                isLoading={toDelete && toDelete.index === index}
                                icon={<FiTrash2 fontSize="1.25rem" />}
                                variant="ghost"
                                aria-label="Delete guestlist name"
                              />
                            </>
                          ) : null}
                        </HStack>
                      </Td>
                    </Tr>
                  ))}
                  {isGuestlistLoading ? (
                    <Tr>
                      <Td colSpan={6}>
                        <HStack w="full" height={24} justifyContent={"center"}>
                          <Spinner />
                        </HStack>
                      </Td>
                    </Tr>
                  ) : null}
                </Tbody>
              </Table>
            </Box>
          </VStack>
        </>
      )}
    </Stack>
  );
};

export default withGuestlistDialog(
  withAddUserDialog(
    withConfirmDialog(withColumnMatchingModal(GuestlistNavSection))
  )
);
